import {MoreHoriz, SaveAs, Tune} from "@mui/icons-material";
import {IconButton, Stack} from "@mui/material";
import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useLocation} from "react-router-dom";
import Constants from "../../../../common/Constants";
import EventManager from "../../../../common/event/EventManager";
import EventManagerContext from "../../../../common/event/EventManagerContext";
import {GridPresets} from "../../../../components/grid/presets/GridPresets";
import {GridPresetsProcessor} from "../../../../components/grid/presets/GridPresetsProcessor";
import {Menu} from "../../../../components/menu/Menu";
import {MenuItem} from "../../../../components/menu/MenuItem";
import Page from "../../../../components/page/Page";
import PageContent from "../../../../components/page/PageContent";
import TitleHeader from "../../../../components/TitleHeader";
import {PersistentStateId} from "../../../../store/common/Grid";
import {getFilterUpdateAction as getElementsFilterUpdateAction, IFilter} from "../../../../store/elements/Elements";
import {getGridPresetsResetAction, getGridPresetsUpdateAction} from "../../../../store/elements/GridPresets";
import {_transl} from "../../../../store/localization/TranslMessasge";
import {IApplicationState} from "../../../../store/Store";
import {CommonTranslationKey} from "../CommonTranslationKey";
import {ErrorTranslationKey} from "../ErrorTranslationKey";
import pagePresetsClient from "../presets/client/PagePresetsClient";
import {PagePresetsDto, PageSettingsDto} from "../presets/client/PagePresetsDto";
import {GridPresetsEventType} from "../presets/GridPresetsEvents";
import PagePresetsManagementDialog from "../presets/PagePresetsManagementDialog";
import {PagePresetsTranslationKey} from "../presets/PagePresetsTranslationKey";
import PagePresetsUpdateDialog from "../presets/PagePresetsUpdateDialog";
import Snackbar from "../snackbar/Snackbar";
import ElementsFilter from "./ElementsFilter";
import ElementsGrid, {ElementsGridType} from "./ElementsGrid";
import {ElementTranslationKey} from "./ElementTranslationKey";
import {ElementsGridDef} from "./RenderMode";

export const ELEMENTS_PAGE_ID: string = "ELEMENTS_PAGE_ID";
export const ELEMENTS_PAGE_PRESETS_ID_QUERY_PARAM = "presets";

export default function ElementsPage() {
    const location = useLocation();
    const pageSize = Constants.LARGE_GRID_PAGE_SIZE;

    const dispatch = useDispatch();

    const eventManager = useContext<EventManager>(EventManagerContext);

    const pageMenuIconRef = useRef<HTMLButtonElement | null>(null);
    const [pageMenuOpened, setPageMenuOpened] = useState<boolean>(false);

    const [presetsLoaded, setPresetsLoaded] = useState<boolean>(false);

    const [defaultPresetsAvailable, setDefaultPresetsAvailable] = useState<boolean>(false);
    const [pagePresetsManagementDialogOpened, setPagePresetsManagementDialogOpened] = useState<boolean>(false);
    const [pagePresetsCreateDialogOpened, setPagePresetsCreateDialogOpened] = useState<boolean>(false);

    const lastFilter = useSelector((state: IApplicationState) => state.pages.elements.filter.submittedFilter.lastFilter);
    const [filter, setFilter] = useState<IFilter>(initFilter(lastFilter));
    const [submittedFilter, setSubmittedFilter] = useState<IFilter>(filter);
    const [filterUpdated, setFilterUpdated] = useState<boolean>(false);

    const lastGridPresets = useSelector((state: IApplicationState) => state.pages.elements.gridPresets.submittedGridPresets.gridPresets);
    const [gridPresets, setGridPresets] = useState<GridPresets | undefined>(initGridPresets(lastGridPresets));
    const [gridPresetsUpdated, setGridPresetsUpdated] = useState<boolean>(false);

    const presetsExtractor = useRef<GridPresetsProcessor>(new GridPresetsProcessor());

    function createDefaultPresets() {
        const normalizationResult = presetsExtractor.current.normalizePresets(ElementsGridDef.getGridColDef(), undefined);
        return normalizationResult.normalizedPresets;
    }

    function initFilter(savedFilter: IFilter): IFilter {
        return {
            nameLike: savedFilter.nameLike || "",
            identifierLike: savedFilter.identifierLike || "",
            searchText: savedFilter.searchText || "",
            validFrom: savedFilter.validFrom || undefined,
            validThru: savedFilter.validThru || undefined,
            selectedTypes: savedFilter.selectedTypes || [],
            selectedLabels: savedFilter.selectedLabels || [],
            selectedCollections: savedFilter.selectedCollections || [],
            selectedStates: savedFilter.selectedStates || [],
            hasStereotype: savedFilter.hasStereotype || undefined,
            stereotypeNameLike: savedFilter.stereotypeNameLike || "",
            maxPageSize: savedFilter.maxPageSize || pageSize,
            pageToken: savedFilter.pageToken || "0",
        };
    }

    function initGridPresets(lastGridPresets: GridPresets): GridPresets | undefined {
        if (lastGridPresets.columns.length === 0) {
            return undefined;
        } else {
            return lastGridPresets;
        }
    }

    const applyPresets = useCallback((pagePresets: PagePresetsDto) => {
        if (pagePresets.settings.gridPresets) {
            setGridPresets(pagePresets.settings.gridPresets);
            dispatch(getGridPresetsUpdateAction(pagePresets.settings.gridPresets));
        }
        if (pagePresets.settings.filter) {
            setFilter(pagePresets.settings.filter as IFilter);
        }
        setFilterUpdated(false);
        setGridPresetsUpdated(false);
    }, [dispatch]);

    const refetchData = useCallback(() => {
        const newFilter = {...filter};
        dispatch(getElementsFilterUpdateAction(newFilter));
        setSubmittedFilter(newFilter);
    }, [dispatch, filter]);

    const refetchDataForPresetsWithFilter = useCallback((filter: IFilter) => {
        dispatch(getElementsFilterUpdateAction(filter));
        setSubmittedFilter(filter);
    }, [dispatch]);

    const applyPresetsWithRefetch = useCallback((pagePresets: PagePresetsDto) => {
        applyPresets(pagePresets);
        if (pagePresets.settings.filter) {
            refetchDataForPresetsWithFilter(pagePresets.settings.filter as IFilter);
        } else {
            refetchData();
        }
    }, [refetchData, refetchDataForPresetsWithFilter, applyPresets]);

    const loadPagePresetsById = useCallback((presetsId: number): void => {
        const promises = [
            pagePresetsClient.find(ELEMENTS_PAGE_ID, presetsId),
            pagePresetsClient.findDefaultPresets(ELEMENTS_PAGE_ID),
        ];

        Promise.allSettled(promises)
                .then((results) => {
                    const [customPresetsResult, defaultPresetsResult] = results;

                    const defaultSettings = defaultPresetsResult.status === "fulfilled" ? defaultPresetsResult.value as PageSettingsDto : undefined;

                    if (customPresetsResult.status === "fulfilled") {
                        applyPresetsWithRefetch(customPresetsResult.value as PagePresetsDto);
                    } else {
                        if (defaultSettings) {
                            if (defaultSettings.gridPresets) {
                                setGridPresets(defaultSettings.gridPresets);
                            }
                            if (defaultSettings.filter) {
                                setFilter(defaultSettings.filter as IFilter);
                            }
                        }
                    }

                    setDefaultPresetsAvailable(defaultSettings !== undefined);
                    setPresetsLoaded(true);
                });
    }, [applyPresetsWithRefetch]);

    const loadDefaultPresets = useCallback((): void => {
        if (!presetsLoaded) {
            pagePresetsClient.findDefaultPresets(ELEMENTS_PAGE_ID)
                    .then(settings => {
                        if (settings && lastGridPresets.columns.length === 0) {
                            setGridPresets(settings.gridPresets);
                            setGridPresetsUpdated(false);
                        }
                        setDefaultPresetsAvailable(settings !== undefined);
                        setPresetsLoaded(true);
                    });
        }
    }, [lastGridPresets, presetsLoaded]);

    const setGridPresetsToDefault = useCallback((): void => {
        pagePresetsClient.findDefaultPresets(ELEMENTS_PAGE_ID)
                .then(settings => {
                    if (settings) {
                        setGridPresets(settings.gridPresets);
                        setGridPresetsUpdated(false);
                        dispatch(getGridPresetsUpdateAction(settings.gridPresets));
                    }
                    setDefaultPresetsAvailable(settings !== undefined);
                    setPresetsLoaded(true);
                });
    }, [dispatch]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const presetsId = params.get(ELEMENTS_PAGE_PRESETS_ID_QUERY_PARAM);
        if (presetsId) {
            loadPagePresetsById(Number(presetsId));
        } else {
            loadDefaultPresets();
        }
    }, [location, loadPagePresetsById, loadDefaultPresets]);

    function saveDefaultPresets(): void {
        if (gridPresets) {
            const settings = {
                gridPresets: gridPresets
            };
            pagePresetsClient.setDefaultPresets(ELEMENTS_PAGE_ID, settings)
                    .then(() => {
                        Snackbar.success(_transl(CommonTranslationKey.DATA_SAVED_SUCCESSFULLY));
                        setDefaultPresetsAvailable(settings !== undefined);
                    })
                    .catch((err) => Snackbar.error(_transl(ErrorTranslationKey.FAILED_TO_SAVE_DATA), err));
            dispatch(getGridPresetsUpdateAction(gridPresets));
        }
    }

    function deleteDefaultPresets(): void {
        pagePresetsClient.deleteDefaultPresets(ELEMENTS_PAGE_ID)
                .then(() => {
                    setGridPresets(undefined);
                    setGridPresetsUpdated(false);
                    setDefaultPresetsAvailable(false);
                    dispatch(getGridPresetsResetAction());

                    eventManager.publishEvent({type: GridPresetsEventType.PRESETS_RESET});
                })
                .catch((err) => {
                    setGridPresets(undefined);
                    setGridPresetsUpdated(false);
                });
    }

    function updateGridPresets(gridPresets: GridPresets): void {
        setGridPresets(gridPresets);
        setGridPresetsUpdated(true);
    }

    function showPresetsManagement() {
        setPagePresetsManagementDialogOpened(true);
        setPageMenuOpened(false);
    }

    function showSavePresetsAsDialog() {
        setPagePresetsCreateDialogOpened(true);
        setPageMenuOpened(false);
    }

    function updateFilter(filter: IFilter): void {
        setFilterUpdated(true);
        setFilter(filter);
    }

    return (
            <>
                <Page>
                    <TitleHeader title={_transl(ElementTranslationKey.ELEMENTS_TITLE)}
                            right={
                                <IconButton onClick={() => setPageMenuOpened(true)}
                                        ref={element => pageMenuIconRef.current = element}>
                                    <MoreHoriz/>
                                </IconButton>
                            }/>
                    <PageContent>
                        <Stack spacing={2}>
                            <ElementsFilter filter={filter}
                                    onFilterChanged={updateFilter}
                                    onSearchClicked={() => refetchData()}/>
                            {presetsLoaded &&
                                    <ElementsGrid
                                            filter={submittedFilter}
                                            refetchData={refetchData}
                                            gridPresets={gridPresets}
                                            onGridPresetsChanged={updateGridPresets}
                                            persistentStateId={PersistentStateId.ELEMENTS_PAGE_GRID}
                                            isDefaultPresetsAvailable={defaultPresetsAvailable}
                                            onLoadDefaultPresets={() => setGridPresetsToDefault()}
                                            onSavePresetsAsDefault={() => saveDefaultPresets()}
                                            onResetDefaultPresets={() => deleteDefaultPresets()}
                                            elementsGridType={ElementsGridType.OVERVIEW}/>
                            }
                        </Stack>
                    </PageContent>
                </Page>

                <Menu open={pageMenuOpened}
                        onClose={() => setPageMenuOpened(false)}
                        anchorEl={pageMenuIconRef.current}>
                    <MenuItem label={_transl(PagePresetsTranslationKey.SAVE_PRESETS_AS)}
                            icon={<SaveAs/>}
                            disabled={!gridPresetsUpdated && !filterUpdated}
                            onClick={() => showSavePresetsAsDialog()}/>
                    <MenuItem label={_transl(PagePresetsTranslationKey.PRESETS_MANAGEMENT)}
                            icon={<Tune/>}
                            onClick={() => showPresetsManagement()}/>
                </Menu>

                {pagePresetsCreateDialogOpened &&
                        <PagePresetsUpdateDialog open={pagePresetsCreateDialogOpened}
                                onDialogClosed={() => setPagePresetsCreateDialogOpened(false)}
                                pageId={ELEMENTS_PAGE_ID}
                                gridPresets={gridPresets ? gridPresets : createDefaultPresets()}
                                filter={filter}/>}

                <PagePresetsManagementDialog open={pagePresetsManagementDialogOpened}
                        onApplyPresets={presets => applyPresetsWithRefetch(presets)}
                        onDialogClosed={() => setPagePresetsManagementDialogOpened(false)}
                        gridId={ELEMENTS_PAGE_ID}/>
            </>
    );
}
